import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  getPayrollConfigs, savePayrollConfig, deletePayrollConfig,
  getHolidays, addHoliday, deleteHoliday,
} from "../../firebase/services";
import {
  DEFAULT_WORK_CONFIG, DEFAULT_RULES, DEFAULT_TAX_CONFIG, DEFAULT_COMPANY_COST,
  DEFAULT_SALES_TIERS, DEFAULT_HIGHEND_CONFIG,
} from "../../firebase/schema";
import { useLanguage } from "../../hooks/useLanguage";
import { useAuth } from "../../hooks/useAuth";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";

// ⚙️ Cấu hình tập trung — mọi quy tắc tính công, lương, thuế, chi phí ở một chỗ.
// Mỗi lần lưu là một phiên bản "áp dụng từ tháng …": tháng trước đó giữ nguyên quy tắc cũ,
// nên lương đã trả không bị tính lại.

const TABS = [
  ["work", "⏱", "cfg.tab_work"],
  ["holiday", "🎌", "cfg.tab_holiday"],
  ["bonus", "🏆", "cfg.tab_bonus"],
  ["tax", "🧾", "cfg.tab_tax"],
  ["cost", "🏢", "cfg.tab_cost"],
  ["rules", "📋", "cfg.tab_rules"],
];

const vnd = (n) => Math.round(Number(n) || 0).toLocaleString("vi-VN");
const clone = (o) => JSON.parse(JSON.stringify(o));

function Field({ label, hint, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Num({ value, onChange, step = 1, min = 0, suffix }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="number" step={step} min={min} value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
      {suffix && <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>{suffix}</span>}
    </div>
  );
}

function Check({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 10 }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ width: "auto" }} />
      {label}
    </label>
  );
}

export default function AdminSettings() {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { cfgFor, reload: reloadProvider } = usePayrollConfig();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "work";

  const [versions, setVersions] = useState([]);
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [holForm, setHolForm] = useState({ name: "", date: "" });

  const loadVersions = async () => setVersions(await getPayrollConfigs({ includeCost: true }));
  const loadHolidays = async () => setHolidays(await getHolidays());
  useEffect(() => { loadVersions(); loadHolidays(); }, []);

  const existing = versions.find(v => v.effectiveFrom === month);

  // Nạp bản nháp: phiên bản đúng tháng nếu đã có; nếu chưa thì lấy quy tắc đang áp dụng
  // cho tháng đó (giữ nguyên thưởng/thuế/chi phí anh đã cài), riêng giờ làm & quy định
  // nạp sẵn giá trị mới nếu tháng đó còn đang theo quy tắc cũ.
  useEffect(() => {
    // cfgFor(month) trả đúng phiên bản của tháng này, hoặc phiên bản gần nhất trước đó,
    // hoặc quy tắc cũ nếu chưa có phiên bản nào
    const cur = cfgFor(month);
    const work = cur.work.legacy ? { ...DEFAULT_WORK_CONFIG } : { ...cur.work };
    delete work.legacy;
    setDraft(clone({
      work,
      rules: cur.rules || { ...DEFAULT_RULES },
      bonus: cur.bonus,
      tax: cur.tax,
      cost: { ...DEFAULT_COMPANY_COST, ...cur.cost },
    }));
    setMsg("");
  }, [month, cfgFor]);

  const set = (section, key, val) => setDraft(d => ({ ...d, [section]: { ...d[section], [key]: val } }));

  const handleSave = async () => {
    const thisMonth = format(new Date(), "yyyy-MM");
    if (month < thisMonth && !window.confirm(t("cfg.past_warn", { month }))) return;
    setSaving(true); setMsg("");
    try {
      const sortedTiers = [...draft.bonus.salesTiers].sort((a, b) => b.minRate - a.minRate);
      await savePayrollConfig(month, { ...draft, bonus: { ...draft.bonus, salesTiers: sortedTiers } }, profile?.name || "Admin");
      await Promise.all([loadVersions(), reloadProvider()]);
      setMsg("✅ " + t("cfg.saved", { month }));
    } catch (err) { setMsg("Lỗi: " + err.message); }
    setSaving(false);
  };

  const handleDelete = async (v) => {
    if (!window.confirm(t("cfg.delete_confirm", { month: v.effectiveFrom }))) return;
    await deletePayrollConfig(v.effectiveFrom, profile?.name || "Admin");
    await Promise.all([loadVersions(), reloadProvider()]);
  };

  // Ví dụ minh họa với lương cơ bản 6.500.000
  const sample = useMemo(() => {
    if (!draft) return null;
    const w = draft.work;
    const daily = 6500000 / (Number(w.standardDays) || 26);
    const hourly = daily / (Number(w.rateHours) || Number(w.standardHours) || 8);
    return { daily, hourly, ot1h: hourly * (Number(w.otRate) || 0) / 100 };
  }, [draft]);

  if (!draft) return <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>{t("common.loading")}</div>;
  const w = draft.work, rules = draft.rules, tax = draft.tax, cost = draft.cost, bonus = draft.bonus;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>⚙️ {t("cfg.title")}</h1>
          <p>{t("cfg.subtitle")}</p>
        </div>
      </div>

      {/* ── Phiên bản theo tháng áp dụng ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{t("cfg.effective_from")}</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 180 }} />
          </div>
          <div style={{ fontSize: 12, color: "#666", flex: 1, minWidth: 220 }}>
            {existing ? t("cfg.editing_existing", { month }) : t("cfg.creating_new", { month })}
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? t("common.saving") : "💾 " + t("cfg.save", { month })}
          </button>
        </div>
        {msg && <div className={`alert ${msg.startsWith("Lỗi") ? "alert-error" : "alert-success"}`} style={{ marginTop: 12 }}>{msg}</div>}

        <div style={{ marginTop: 14, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("cfg.history")}</div>
          {versions.length === 0 ? (
            <div style={{ color: "#888" }}>{t("cfg.no_versions")}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {versions.map(v => (
                <div key={v.effectiveFrom} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
                  padding: "6px 10px", borderRadius: 6, background: v.effectiveFrom === month ? "#FDECEA" : "#FAFAFA" }}>
                  <b style={{ minWidth: 90 }}>{t("cfg.from")} {v.effectiveFrom.slice(5)}/{v.effectiveFrom.slice(0, 4)}</b>
                  <span style={{ color: "#888", flex: 1 }}>
                    {v.work?.standardHours ?? "—"}h/{t("x.workday")} · OT {v.work?.otRate ?? "—"}% / {v.work?.weeklyOffOtRate ?? "—"}% / {v.work?.holidayOtRate ?? "—"}%
                    {v.updatedBy ? ` · ${v.updatedBy}` : ""}
                    {v.updatedAt?.seconds ? ` · ${new Date(v.updatedAt.seconds * 1000).toLocaleDateString("vi-VN")}` : ""}
                  </span>
                  <button className="btn btn-sm" onClick={() => setMonth(v.effectiveFrom)}>{t("common.edit")}</button>
                  <button className="btn btn-sm" style={{ color: "#C8152A" }} onClick={() => handleDelete(v)}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ color: "#888", marginTop: 6 }}>{t("cfg.legacy_note")}</div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map(([key, icon, label]) => (
          <button key={key} className={`tab ${tab === key ? "active" : ""}`}
            onClick={() => setParams({ tab: key })}>{icon} {t(label)}</button>
        ))}
      </div>

      {tab === "work" && (
        <div className="card">
          <div className="grid-2" style={{ gap: 12 }}>
            <Field label={t("cfg.standard_days")} hint={t("cfg.standard_days_hint")}>
              <Num value={w.standardDays} onChange={v => set("work", "standardDays", v)} suffix={t("x.workday")} />
            </Field>
            <Field label={t("cfg.standard_hours")} hint={t("cfg.standard_hours_hint")}>
              <Num value={w.standardHours} step={0.5} onChange={v => set("work", "standardHours", v)} suffix="h" />
            </Field>
            <Field label={t("cfg.rate_hours")} hint={t("cfg.rate_hours_hint")}>
              <Num value={w.rateHours} step={0.5} onChange={v => set("work", "rateHours", v)} suffix="h" />
            </Field>
            <Field label={t("cfg.half_shift")} hint={t("cfg.half_shift_hint")}>
              <Num value={w.halfShiftHours} step={0.5} onChange={v => set("work", "halfShiftHours", v)} suffix="h" />
            </Field>
            <Field label={t("cfg.tolerance")} hint={t("cfg.tolerance_hint")}>
              <Num value={w.shortTolerance} step={0.25} onChange={v => set("work", "shortTolerance", v)} suffix="h" />
            </Field>
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, margin: "6px 0 2px" }}>{t("cfg.ot_tiers")}</div>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>{t("cfg.ot_tiers_note")}</div>
          <div className="grid-3" style={{ gap: 12 }}>
            <Field label={t("cfg.ot_rate")} hint={t("cfg.ot_rate_hint")}>
              <Num value={w.otRate} step={10} onChange={v => set("work", "otRate", v)} suffix="%" />
            </Field>
            <Field label={t("cfg.weekly_off_ot")} hint={t("cfg.weekly_off_ot_hint")}>
              <Num value={w.weeklyOffOtRate} step={10} onChange={v => set("work", "weeklyOffOtRate", v)} suffix="%" />
            </Field>
            <Field label={t("cfg.holiday_ot")} hint={t("cfg.holiday_ot_hint")}>
              <Num value={w.holidayOtRate} step={10} onChange={v => set("work", "holidayOtRate", v)} suffix="%" />
            </Field>
          </div>
          <Check checked={w.otRequiresApproval} onChange={v => set("work", "otRequiresApproval", v)} label={t("cfg.ot_approval")} />
          <Check checked={w.otTaxExempt} onChange={v => set("work", "otTaxExempt", v)} label={t("cfg.ot_tax_exempt")} />
          {sample && (
            <div className="alert alert-info" style={{ fontSize: 12 }}>
              💡 {t("cfg.example_work", { daily: vnd(sample.daily), hourly: vnd(sample.hourly), ot: vnd(sample.ot1h), h: w.rateHours || w.standardHours })}
            </div>
          )}
        </div>
      )}

      {tab === "holiday" && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="grid-2" style={{ gap: 12 }}>
              <Field label={t("cfg.holiday_paid")} hint={t("cfg.holiday_paid_hint")}>
                <Num value={w.holidayPaidRate} step={10} onChange={v => set("work", "holidayPaidRate", v)} suffix="%" />
              </Field>
              <Field label={t("cfg.holiday_ot")}>
                <div style={{ fontSize: 13, padding: "8px 0" }}>
                  <b>{w.holidayOtRate}%</b>{" "}
                  <a href="#" onClick={e => { e.preventDefault(); setParams({ tab: "work" }); }} style={{ fontSize: 12 }}>
                    {t("cfg.edit_in_ot_tab")}
                  </a>
                </div>
              </Field>
            </div>
            <div className="alert alert-info" style={{ fontSize: 12 }}>
              💡 {t("cfg.example_holiday", { off: w.holidayPaidRate, on: Number(w.holidayPaidRate) + Number(w.holidayOtRate) })}
            </div>
          </div>

          <div className="card">
            <div className="card-title">{t("holiday.title")}</div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>{t("cfg.holiday_list_note")}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <input placeholder={t("holiday.name")} value={holForm.name} style={{ flex: "1 1 180px" }}
                onChange={e => setHolForm(f => ({ ...f, name: e.target.value }))} />
              <input type="date" value={holForm.date} style={{ width: 170 }}
                onChange={e => setHolForm(f => ({ ...f, date: e.target.value }))} />
              <button className="btn btn-primary" disabled={!holForm.name || !holForm.date}
                onClick={async () => { await addHoliday({ ...holForm, multiplier: 3 }); setHolForm({ name: "", date: "" }); loadHolidays(); }}>
                + {t("holiday.add")}
              </button>
            </div>
            <table>
              <tbody>
                {[...holidays].sort((a, b) => b.date.localeCompare(a.date)).map(h => (
                  <tr key={h.id}>
                    <td style={{ width: 110 }}>{h.date.split("-").reverse().join("/")}</td>
                    <td>{h.name}</td>
                    <td style={{ width: 50 }}>
                      <button className="btn btn-sm btn-danger" onClick={async () => { await deleteHoliday(h.id); loadHolidays(); }}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "bonus" && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">{t("salary.sales_bonus")}</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{t("salary.sales_bonus_desc")}</div>
              </div>
              <button className="btn btn-sm" onClick={() => set("bonus", "salesTiers", [...bonus.salesTiers, { minRate: 0.5, bonus: 0 }])}>
                + {t("salary.add_tier")}
              </button>
            </div>
            {bonus.salesTiers.map((tier, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#888" }}>≥</span>
                <input type="number" step="5" value={Math.round(tier.minRate * 100)} style={{ width: 80 }}
                  onChange={e => set("bonus", "salesTiers", bonus.salesTiers.map((x, j) => j === i ? { ...x, minRate: Number(e.target.value) / 100 } : x))} />
                <span style={{ fontSize: 12, color: "#888" }}>% target →</span>
                <input type="number" step="500000" value={tier.bonus} style={{ width: 150 }}
                  onChange={e => set("bonus", "salesTiers", bonus.salesTiers.map((x, j) => j === i ? { ...x, bonus: Number(e.target.value) } : x))} />
                <span style={{ fontSize: 12, color: "#888" }}>đ</span>
                <button className="btn btn-sm btn-danger" disabled={bonus.salesTiers.length <= 1}
                  onClick={() => set("bonus", "salesTiers", bonus.salesTiers.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="btn btn-sm" style={{ marginTop: 6 }} onClick={() => set("bonus", "salesTiers", clone(DEFAULT_SALES_TIERS))}>
              {t("salary.reset_default")}
            </button>
          </div>
          <div className="card">
            <div className="card-title">{t("salary.highend_bonus")}</div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>{t("salary.he_model_desc")}</div>
            <div className="grid-2" style={{ gap: 12 }}>
              <Field label={t("salary.he_unit_rate")}>
                <Num value={bonus.highendConfig.unitRate} step={50000}
                  onChange={v => set("bonus", "highendConfig", { ...bonus.highendConfig, unitRate: v })} suffix="đ" />
              </Field>
              <Field label={t("salary.he_over_rate")}>
                <Num value={bonus.highendConfig.overRate} step={50000}
                  onChange={v => set("bonus", "highendConfig", { ...bonus.highendConfig, overRate: v })} suffix="đ" />
              </Field>
            </div>
            <button className="btn btn-sm" onClick={() => set("bonus", "highendConfig", { ...DEFAULT_HIGHEND_CONFIG })}>
              {t("salary.reset_default")}
            </button>
          </div>
        </>
      )}

      {tab === "tax" && (
        <div className="card">
          <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>{t("tax.legal_note")}</div>
          <div className="grid-2" style={{ gap: 12 }}>
            <Field label={t("tax.personal_ded")}><Num value={tax.personalDeduction} step={100000} onChange={v => set("tax", "personalDeduction", v)} suffix="đ" /></Field>
            <Field label={t("tax.dependent_ded")}><Num value={tax.dependentDeduction} step={100000} onChange={v => set("tax", "dependentDeduction", v)} suffix="đ" /></Field>
            <Field label={t("tax.meal_cap")}><Num value={tax.mealAllowanceCap} step={50000} onChange={v => set("tax", "mealAllowanceCap", v)} suffix="đ" /></Field>
            <Field label={t("tax.probation_months")}><Num value={tax.probationMonths} onChange={v => set("tax", "probationMonths", v)} /></Field>
            <Field label={t("tax.probation_mode")} hint={tax.probationTaxMode === "flat" ? t("tax.probation_flat_hint") : t("tax.probation_prog_hint")}>
              <select value={tax.probationTaxMode || "progressive"} onChange={e => set("tax", "probationTaxMode", e.target.value)}>
                <option value="flat">{t("tax.probation_flat")}</option>
                <option value="progressive">{t("tax.probation_prog")}</option>
              </select>
            </Field>
            {tax.probationTaxMode === "flat" && (
              <>
                <Field label={t("tax.probation_rate")}><Num value={tax.probationFlatRate} step={0.5} onChange={v => set("tax", "probationFlatRate", v)} suffix="%" /></Field>
                <Field label={t("tax.probation_min")}><Num value={tax.probationFlatMin} step={100000} onChange={v => set("tax", "probationFlatMin", v)} suffix="đ" /></Field>
              </>
            )}
            <Field label={t("tax.emp_si")}><Num value={tax.empSiPct} step={0.1} onChange={v => set("tax", "empSiPct", v)} suffix="%" /></Field>
            <Field label={t("tax.emp_hi")}><Num value={tax.empHiPct} step={0.1} onChange={v => set("tax", "empHiPct", v)} suffix="%" /></Field>
            <Field label={t("tax.emp_ui")}><Num value={tax.empUiPct} step={0.1} onChange={v => set("tax", "empUiPct", v)} suffix="%" /></Field>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, margin: "8px 0 4px" }}>{t("tax.brackets")}</div>
          {(tax.brackets || []).map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#888", width: 44 }}>{t("tax.tier")} {i + 1}</span>
              <input type="number" placeholder={t("tax.up_to")} value={b.upTo ?? ""} style={{ flex: 1 }}
                onChange={e => set("tax", "brackets", tax.brackets.map((x, j) => j === i ? { ...x, upTo: e.target.value === "" ? null : Number(e.target.value) } : x))} />
              <input type="number" step="0.1" value={b.rate} style={{ width: 80 }}
                onChange={e => set("tax", "brackets", tax.brackets.map((x, j) => j === i ? { ...x, rate: Number(e.target.value) } : x))} />
              <span style={{ fontSize: 12, color: "#888" }}>%</span>
              <button className="btn btn-sm" onClick={() => set("tax", "brackets", tax.brackets.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>{t("tax.up_to_note")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => set("tax", "brackets", [...(tax.brackets || []), { upTo: null, rate: 35 }])}>{t("salary.add_tier")}</button>
            <button className="btn btn-sm" onClick={() => setDraft(d => ({ ...d, tax: clone(DEFAULT_TAX_CONFIG) }))}>{t("salary.reset_default")}</button>
          </div>
        </div>
      )}

      {tab === "cost" && (
        <div className="card">
          <div className="grid-3" style={{ gap: 12 }}>
            <Field label="BHXH DN"><Num value={cost.siPct} step={0.1} onChange={v => set("cost", "siPct", v)} suffix="%" /></Field>
            <Field label="BHYT DN"><Num value={cost.hiPct} step={0.1} onChange={v => set("cost", "hiPct", v)} suffix="%" /></Field>
            <Field label="BHTN DN"><Num value={cost.uiPct} step={0.1} onChange={v => set("cost", "uiPct", v)} suffix="%" /></Field>
            <Field label={t("cc.union")}><Num value={cost.unionPct} step={0.1} onChange={v => set("cost", "unionPct", v)} suffix="%" /></Field>
            <Field label={t("cc.pai")}><Num value={cost.pai} step={10000} onChange={v => set("cost", "pai", v)} suffix="đ" /></Field>
            <Field label="VAT"><Num value={cost.vatPct} step={0.1} onChange={v => set("cost", "vatPct", v)} suffix="%" /></Field>
            <Field label={t("cc.usd_rate")}><Num value={cost.usdRate} step={100} onChange={v => set("cost", "usdRate", v)} suffix="đ" /></Field>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, margin: "8px 0 4px" }}>{t("cc.tiers")}</div>
          {(cost.mgmtTiers || []).map((tier, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: "#888" }}>≤</span>
              <input type="number" value={tier.maxStaff} style={{ width: 70 }}
                onChange={e => set("cost", "mgmtTiers", cost.mgmtTiers.map((x, j) => j === i ? { ...x, maxStaff: Number(e.target.value) } : x))} />
              <span style={{ fontSize: 12, color: "#888" }}>{t("cfg.people")} →</span>
              <input type="number" step="50000" value={tier.fee} style={{ width: 140 }}
                onChange={e => set("cost", "mgmtTiers", cost.mgmtTiers.map((x, j) => j === i ? { ...x, fee: Number(e.target.value) } : x))} />
              <span style={{ fontSize: 12, color: "#888" }}>đ/{t("cfg.person")}</span>
              <button className="btn btn-sm" onClick={() => set("cost", "mgmtTiers", cost.mgmtTiers.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={() => set("cost", "mgmtTiers", [...(cost.mgmtTiers || []), { maxStaff: 20, fee: 1000000 }])}>
            {t("salary.add_tier")}
          </button>
        </div>
      )}

      {tab === "rules" && (
        <div className="card">
          <div className="grid-2" style={{ gap: 12 }}>
            <Field label={t("cfg.closing_day")} hint={t("cfg.closing_day_hint")}>
              <Num value={rules.closingDay} min={1} onChange={v => set("rules", "closingDay", v)} />
            </Field>
            <Field label={t("cfg.max_appeals")} hint={t("cfg.max_appeals_hint")}>
              <Num value={rules.maxAppealsPerMonth} onChange={v => set("rules", "maxAppealsPerMonth", v)} />
            </Field>
            <Field label={t("cfg.remind_hour")} hint={t("cfg.remind_hour_hint")}>
              <Num value={rules.checkoutRemindHour} min={0} onChange={v => set("rules", "checkoutRemindHour", v)} suffix="h" />
            </Field>
          </div>
        </div>
      )}
    </>
  );
}
